/**
 * /admin/database — read-only DB schema browser with live monitoring + AI chat.
 *
 * Left pane: app tree, each app expandable to show its models with row counts.
 *   - "Recently active" pinned section at top: models whose row_count grew
 *     since the page loaded (delta > 0). Refreshed every 15s while live mode is on.
 * Right pane: selected model — field list + sample 25 rows.
 *   - Live mode (Off / 2s / 5s / 10s) auto-polls the rows endpoint.
 *   - New rows (PK not seen in the previous fetch) flash yellow for ~3s.
 *   - Click any row to open a detail drawer with the full untruncated payload.
 *   - Polling pauses when the tab is hidden (Page Visibility API).
 * Optional dockable chat panel: ask in plain English, the bot uses Claude
 *   Sonnet 4.6 + a 6-tool read-only API surface to find / inspect rows.
 *   schema_pointers in the bot's reply render as click-through chips that
 *   jump straight to the model + open the row-detail drawer.
 *
 * Backend:
 *   GET  /api/admin/v2/schema/
 *   GET  /api/admin/v2/schema/<app>/<model>/
 *   GET  /api/admin/v2/schema/<app>/<model>/rows/?limit=&offset=&search=
 *   POST /api/admin/v2/schema/chat/         { messages, model? }
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import {
  ChevronRight, ChevronDown, Database, Table2, Search,
  Activity, Pause, X, ArrowUpRight, Sparkles, Send, Loader2,
  ExternalLink,
} from "lucide-react";
import {
  getSchemaTree, getSchemaModelRows, postSchemaChat,
} from "../../api/schema.admin.api";

const LIVE_OPTIONS = [
  { value: 0, label: "Off" },
  { value: 2, label: "2s" },
  { value: 5, label: "5s" },
  { value: 10, label: "10s" },
];
const TREE_REFRESH_MS = 15000; // tree counts are expensive — slower cadence
const PAGE = 25;

const lsGet = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : JSON.parse(v);
  } catch { return fallback; }
};
const lsSet = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
};

const formatTime = (d) => {
  if (!d) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

export default function AdminDatabase() {
  const [tree, setTree] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [expanded, setExpanded] = useState(() => lsGet("zcd_db_expanded", {}));
  const [selected, setSelected] = useState(() => lsGet("zcd_db_selected", null));
  const [rows, setRows] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loadingRows, setLoadingRows] = useState(false);
  const [page, setPage] = useState(0);

  const [live, setLive] = useState(() => lsGet("zcd_db_live", 0));
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [prevPks, setPrevPks] = useState(new Set());
  const [newPks, setNewPks] = useState(new Set());
  const [baselineCounts, setBaselineCounts] = useState({}); // first-seen row_count per app.model
  const [rowDetail, setRowDetail] = useState(null);
  const [docVisible, setDocVisible] = useState(
    typeof document === "undefined" ? true : document.visibilityState !== "hidden",
  );

  // ── Chat panel state ────────────────────────────────────────────────────
  const [chatOpen, setChatOpen] = useState(() => lsGet("zcd_db_chat_open", false));
  const [chatMessages, setChatMessages] = useState(() => lsGet("zcd_db_chat_msgs", []));
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  useEffect(() => lsSet("zcd_db_chat_open", chatOpen), [chatOpen]);
  useEffect(() => lsSet("zcd_db_chat_msgs", chatMessages.slice(-40)), [chatMessages]);

  // ── persistence ─────────────────────────────────────────────────────────
  useEffect(() => lsSet("zcd_db_expanded", expanded), [expanded]);
  useEffect(() => lsSet("zcd_db_selected", selected), [selected]);
  useEffect(() => lsSet("zcd_db_live", live), [live]);

  // ── search debounce ─────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ── document visibility (pause polling when tab hidden) ─────────────────
  useEffect(() => {
    const onVis = () => setDocVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // ── helpers ─────────────────────────────────────────────────────────────
  const selectedModelMeta = useMemo(() => (
    selected
      ? tree.find((a) => a.app_label === selected.app)
          ?.models.find((m) => m.name === selected.model)
      : null
  ), [tree, selected]);

  const pkColumn = useMemo(() => {
    if (!selectedModelMeta) return null;
    const fields = Array.isArray(selectedModelMeta.fields) ? selectedModelMeta.fields : [];
    const pkField = fields.find((f) => f.primary_key);
    return pkField?.name || "id";
  }, [selectedModelMeta]);

  const fetchTree = useCallback(async ({ isInitial = false } = {}) => {
    try {
      const r = await getSchemaTree();
      const apps = r.data.apps || [];
      setTree(apps);
      if (isInitial) {
        const baseline = {};
        for (const a of apps) {
          for (const m of a.models) {
            baseline[`${a.app_label}.${m.name}`] = m.row_count ?? 0;
          }
        }
        setBaselineCounts(baseline);
      }
    } finally {
      if (isInitial) setLoadingTree(false);
    }
  }, []);

  const fetchRows = useCallback(async ({ silent = false, resetPage = false } = {}) => {
    if (!selected) return;
    if (!silent) setLoadingRows(true);
    const useOffset = resetPage ? 0 : page * PAGE;
    try {
      const r = await getSchemaModelRows(selected.app, selected.model, {
        limit: PAGE, offset: useOffset, search,
      });
      const data = r.data;
      // Compute newly-arrived PKs vs previous fetch.
      if (pkColumn && Array.isArray(data?.rows)) {
        const currentPks = new Set(data.rows.map((row) => String(row[pkColumn])));
        if (prevPks.size > 0) {
          const fresh = new Set();
          for (const id of currentPks) if (!prevPks.has(id)) fresh.add(id);
          if (fresh.size > 0) {
            setNewPks(fresh);
            // fade highlight after 3s
            setTimeout(() => setNewPks(new Set()), 3000);
          }
        }
        setPrevPks(currentPks);
      }
      setRows(data);
      if (resetPage) setPage(0);
      setLastFetchedAt(new Date());
    } catch {
      setRows({ error: "failed" });
    } finally {
      if (!silent) setLoadingRows(false);
    }
  }, [selected, page, search, pkColumn, prevPks]);

  // ── initial tree load ───────────────────────────────────────────────────
  useEffect(() => { fetchTree({ isInitial: true }); }, [fetchTree]);

  // ── row fetch on selection / search change ──────────────────────────────
  useEffect(() => {
    if (!selected) return;
    // selection/search changed → reset PK tracking so the first batch isn't all "new"
    setPrevPks(new Set());
    setNewPks(new Set());
    setPage(0);
    fetchRows({ resetPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.app, selected?.model, search]);

  // ── live polling: rows ──────────────────────────────────────────────────
  useEffect(() => {
    if (!live || !selected || !docVisible) return;
    const id = setInterval(() => { fetchRows({ silent: true }); }, live * 1000);
    return () => clearInterval(id);
  }, [live, selected, docVisible, fetchRows]);

  // ── live polling: tree counts (slower cadence) ──────────────────────────
  useEffect(() => {
    if (!live || !docVisible) return;
    const id = setInterval(() => { fetchTree(); }, TREE_REFRESH_MS);
    return () => clearInterval(id);
  }, [live, docVisible, fetchTree]);

  // ── recently-active models (delta > 0 since baseline) ───────────────────
  const recentlyActive = useMemo(() => {
    if (!Object.keys(baselineCounts).length) return [];
    const out = [];
    for (const a of tree) {
      for (const m of a.models) {
        const key = `${a.app_label}.${m.name}`;
        const base = baselineCounts[key];
        const now = m.row_count ?? 0;
        if (base !== undefined && now > base) {
          out.push({ app: a.app_label, model: m.name, delta: now - base, row_count: now });
        }
      }
    }
    out.sort((x, y) => y.delta - x.delta);
    return out.slice(0, 8);
  }, [tree, baselineCounts]);

  const loadPage = (p) => {
    setLoadingRows(true);
    getSchemaModelRows(selected.app, selected.model, { limit: PAGE, offset: p * PAGE, search })
      .then((r) => {
        setRows(r.data);
        setPage(p);
        setLastFetchedAt(new Date());
        if (pkColumn && Array.isArray(r.data?.rows)) {
          setPrevPks(new Set(r.data.rows.map((row) => String(row[pkColumn]))));
        }
        setNewPks(new Set());
      })
      .finally(() => setLoadingRows(false));
  };

  const onSelectModel = (app, model) => {
    setSelected({ app, model });
    setSearchInput("");
    setSearch("");
  };

  // Open a row that the chatbot surfaced. Navigates the tree to the
  // model and pops the detail drawer using the row payload the bot
  // already returned (no extra fetch).
  const openPointer = (pointer) => {
    if (!pointer) return;
    if (pointer.app && pointer.model) onSelectModel(pointer.app, pointer.model);
    if (pointer.row) setRowDetail(pointer.row);
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    const next = [...chatMessages, { role: "user", content: text }];
    setChatMessages(next);
    setChatInput("");
    setChatBusy(true);
    try {
      const r = await postSchemaChat(
        next.map(({ role, content }) => ({ role, content })),
      );
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: r.data.reply || "(no response)",
          tool_calls: r.data.tool_calls || [],
          schema_pointers: r.data.schema_pointers || [],
        },
      ]);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || "request failed";
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${msg}`, error: true },
      ]);
    } finally {
      setChatBusy(false);
    }
  };

  const clearChat = () => {
    setChatMessages([]);
    setChatInput("");
  };

  // ── Esc closes the row-detail drawer ────────────────────────────────────
  useEffect(() => {
    if (!rowDetail) return;
    const onKey = (e) => { if (e.key === "Escape") setRowDetail(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rowDetail]);

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* ═════════ Left — app tree ═════════ */}
      <div className="w-72 border-r border-border-default overflow-y-auto p-3 text-sm">
        <div className="flex items-center gap-2 mb-3 text-text-secondary">
          <Database className="w-4 h-4" />
          <h2 className="font-semibold">Schema</h2>
        </div>

        {/* Recently active — only render when something has grown */}
        {recentlyActive.length > 0 && (
          <div className="mb-3 rounded border border-amber-400/30 bg-amber-400/5 p-2">
            <div className="flex items-center gap-1.5 mb-1.5 text-amber-300">
              <Activity className="w-3.5 h-3.5" />
              <span className="text-[11px] font-semibold uppercase tracking-wide">
                Recently active
              </span>
            </div>
            {recentlyActive.map((r) => (
              <button
                key={`${r.app}.${r.model}`}
                onClick={() => onSelectModel(r.app, r.model)}
                className={`w-full flex items-center gap-1.5 px-1.5 py-1 rounded text-left text-xs ${
                  selected?.app === r.app && selected?.model === r.model
                    ? "bg-bg-tertiary"
                    : "hover:bg-bg-secondary/30"
                }`}
                title={`${r.app}.${r.model} · ${r.row_count.toLocaleString()} rows`}
              >
                <ArrowUpRight className="w-3 h-3 text-amber-300" />
                <span className="flex-1 truncate">
                  <span className="text-text-secondary">{r.app}.</span>{r.model}
                </span>
                <span className="text-[10px] font-mono text-amber-300">+{r.delta}</span>
              </button>
            ))}
          </div>
        )}

        {loadingTree ? (
          <div className="text-xs text-text-secondary">Loading…</div>
        ) : (
          tree.map((app) => (
            <div key={app.app_label} className="mb-1">
              <button
                onClick={() =>
                  setExpanded({ ...expanded, [app.app_label]: !expanded[app.app_label] })
                }
                className="w-full flex items-center gap-1 px-1 py-1 rounded hover:bg-bg-secondary/40 text-left"
              >
                {expanded[app.app_label] ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
                <span className="flex-1">{app.app_label}</span>
                <span className="text-[10px] text-text-secondary">
                  {app.model_count}
                </span>
              </button>
              {expanded[app.app_label] && (
                <div className="ml-5 mt-0.5">
                  {app.models.map((m) => {
                    const key = `${app.app_label}.${m.name}`;
                    const base = baselineCounts[key];
                    const grew = base !== undefined && (m.row_count ?? 0) > base;
                    return (
                      <button
                        key={m.name}
                        onClick={() => onSelectModel(app.app_label, m.name)}
                        className={`w-full flex items-center gap-1.5 px-1.5 py-1 rounded text-left text-xs ${
                          selected?.app === app.app_label && selected?.model === m.name
                            ? "bg-bg-tertiary"
                            : "hover:bg-bg-secondary/30"
                        }`}
                      >
                        <Table2 className="w-3 h-3 text-text-secondary" />
                        <span className="flex-1 truncate">{m.name}</span>
                        {grew && (
                          <span className="text-[10px] font-mono text-amber-300">
                            +{(m.row_count ?? 0) - base}
                          </span>
                        )}
                        <span className="text-[10px] text-text-secondary">
                          {m.row_count?.toLocaleString() ?? "?"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* ═════════ Right — model detail ═════════ */}
      <div className="flex-1 overflow-y-auto p-6">
        {!selected ? (
          <div className="text-sm text-text-secondary">
            Select a model from the left to inspect its fields and sample rows.
            <div className="mt-3 text-xs">
              Tip: toggle <span className="text-amber-300">Live</span> on the
              right pane to watch new rows arrive in real time.
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold">
                  {selected.app}.{selected.model}
                </h1>
                {selectedModelMeta && (
                  <p className="text-xs text-text-secondary mt-1">
                    Table <code>{selectedModelMeta.table_name}</code> ·{" "}
                    {selectedModelMeta.row_count?.toLocaleString() ?? "?"} rows ·{" "}
                    {selectedModelMeta.field_count} fields
                  </p>
                )}
              </div>

              {/* Live controls */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex items-center gap-1 text-xs text-text-secondary">
                  {live ? (
                    <Activity className={`w-3.5 h-3.5 ${docVisible ? "text-emerald-400 animate-pulse" : "text-text-secondary"}`} />
                  ) : (
                    <Pause className="w-3.5 h-3.5" />
                  )}
                  <span>Live</span>
                </div>
                <div className="flex rounded border border-border-default overflow-hidden">
                  {LIVE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setLive(opt.value)}
                      className={`px-2 py-0.5 text-xs ${
                        live === opt.value
                          ? "bg-emerald-400/20 text-emerald-200"
                          : "hover:bg-bg-secondary/40 text-text-secondary"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {lastFetchedAt && (
                  <span className="text-[10px] text-text-secondary font-mono">
                    {formatTime(lastFetchedAt)}
                  </span>
                )}
              </div>
            </div>

            {live > 0 && !docVisible && (
              <div className="mt-3 text-[11px] text-text-secondary border-l-2 border-amber-400/40 pl-2">
                Polling paused — tab in background.
              </div>
            )}

            {/* Field list */}
            {selectedModelMeta && (
              <div className="mt-5">
                <h2 className="text-sm font-semibold mb-2">Fields</h2>
                <div className="overflow-x-auto border border-border-default rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-bg-secondary/40 text-text-secondary">
                      <tr>
                        <th className="text-left p-2">Name</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Relation</th>
                        <th className="text-left p-2">Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedModelMeta.fields || []).map((f) => (
                        <tr key={f.name} className="border-t border-border-default">
                          <td className="p-2 font-mono">{f.name}</td>
                          <td className="p-2">{f.type}</td>
                          <td className="p-2 text-text-secondary">
                            {f.related_to || ""}
                          </td>
                          <td className="p-2 text-text-secondary">
                            {[
                              f.primary_key && "PK",
                              f.unique && "UQ",
                              !f.null && "NN",
                              f.max_length && `len ${f.max_length}`,
                            ].filter(Boolean).join(" · ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Sample rows */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <h2 className="text-sm font-semibold">Sample rows</h2>
                  {newPks.size > 0 && (
                    <span className="text-[11px] font-mono text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 px-1.5 py-0.5 rounded">
                      +{newPks.size} new
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1.5 w-3.5 h-3.5 text-text-secondary" />
                    <input
                      type="text"
                      placeholder="Search text columns…"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="pl-7 pr-2 py-1 text-xs rounded border border-border-default bg-bg-secondary/30 w-48"
                    />
                  </div>
                </div>
              </div>

              {loadingRows || !rows ? (
                <div className="text-xs text-text-secondary">Loading rows…</div>
              ) : rows.error ? (
                <div className="text-xs text-red-300">{rows.error}</div>
              ) : rows.rows?.length === 0 ? (
                <div className="text-xs text-text-secondary">No rows.</div>
              ) : (
                <>
                  <div className="overflow-x-auto border border-border-default rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-bg-secondary/40 text-text-secondary sticky top-0">
                        <tr>
                          {(rows?.columns || []).map((c) => (
                            <th key={c} className="text-left p-2 whitespace-nowrap">
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(rows?.rows || []).map((r, i) => {
                          const pkVal = pkColumn ? String(r[pkColumn]) : `idx-${i}`;
                          const isNew = newPks.has(pkVal);
                          return (
                            <tr
                              key={pkVal}
                              onClick={() => setRowDetail(r)}
                              className={`border-t border-border-default cursor-pointer transition-colors ${
                                isNew
                                  ? "bg-emerald-400/15 ring-1 ring-emerald-400/40"
                                  : "hover:bg-bg-secondary/30"
                              }`}
                            >
                              {(rows.columns || []).map((c) => (
                                <td
                                  key={c}
                                  className="p-2 font-mono text-[10px] max-w-[20rem] truncate"
                                  title={String(r[c] ?? "")}
                                >
                                  {r[c] === null ? (
                                    <span className="text-text-secondary">null</span>
                                  ) : (
                                    String(r[c])
                                  )}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-text-secondary">
                    <span>
                      {rows.offset + 1}–{Math.min(rows.offset + rows.limit, rows.total_count)} of{" "}
                      {rows.total_count.toLocaleString()}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => loadPage(page - 1)}
                        disabled={page === 0}
                        className="px-2 py-0.5 rounded border border-border-default disabled:opacity-40"
                      >
                        Prev
                      </button>
                      <button
                        onClick={() => loadPage(page + 1)}
                        disabled={rows.offset + rows.limit >= rows.total_count}
                        className="px-2 py-0.5 rounded border border-border-default disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* ═════════ Chat panel ═════════ */}
      <ChatPanel
        open={chatOpen}
        onToggle={() => setChatOpen((v) => !v)}
        messages={chatMessages}
        input={chatInput}
        setInput={setChatInput}
        onSend={sendChat}
        busy={chatBusy}
        onClear={clearChat}
        onOpenPointer={openPointer}
      />

      {/* ═════════ Row detail drawer ═════════ */}
      {rowDetail && (
        <RowDetailDrawer
          row={rowDetail}
          columns={rows?.columns || Object.keys(rowDetail)}
          onClose={() => setRowDetail(null)}
          modelLabel={selected ? `${selected.app}.${selected.model}` : ""}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Row detail drawer
// ────────────────────────────────────────────────────────────────────────────

function RowDetailDrawer({ row, columns, onClose, modelLabel }) {
  const panelRef = useRef(null);
  useDialogDismiss({ open: true, onClose, ref: panelRef });
  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
        role="presentation"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Row detail ${modelLabel || ""}`}
        className="fixed top-0 right-0 h-full w-[32rem] max-w-full bg-bg-primary border-l border-border-default z-50 flex flex-col"
      >
        <div className="flex items-center justify-between p-3 border-b border-border-default">
          <div className="text-xs text-text-secondary">
            <span className="font-semibold text-text-primary">Row detail</span>
            {modelLabel && <span className="ml-2">· {modelLabel}</span>}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-bg-secondary/40"
            title="Close (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-3 text-xs">
          <table className="w-full">
            <tbody>
              {columns.map((c) => (
                <tr key={c} className="border-b border-border-default/50 align-top">
                  <td className="p-1.5 font-mono text-text-secondary w-40 break-all">{c}</td>
                  <td className="p-1.5 font-mono whitespace-pre-wrap break-all">
                    {renderValue(row[c])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function renderValue(v) {
  if (v === null || v === undefined) {
    return <span className="text-text-secondary">null</span>;
  }
  if (typeof v === "string") {
    // try JSON pretty-print if it looks like a JSON object/array
    const trimmed = v.trim();
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch { /* fall through */ }
    }
    return v;
  }
  if (typeof v === "object") {
    return JSON.stringify(v, null, 2);
  }
  return String(v);
}

// ────────────────────────────────────────────────────────────────────────────
// Chat panel
// ────────────────────────────────────────────────────────────────────────────

function ChatPanel({
  open, onToggle, messages, input, setInput, onSend, busy, onClear,
  onOpenPointer,
}) {
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  if (!open) {
    return (
      <div className="w-10 border-l border-border-default flex flex-col items-center py-3 bg-bg-secondary/20">
        <button
          onClick={onToggle}
          className="p-1.5 rounded hover:bg-bg-secondary/60 text-violet-300"
          title="Open AI chat"
        >
          <Sparkles className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="w-[26rem] border-l border-border-default flex flex-col bg-bg-secondary/10">
      <div className="flex items-center justify-between p-3 border-b border-border-default">
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 text-violet-300" />
          <span className="font-semibold">DB Co-pilot</span>
          <span className="text-[10px] text-text-secondary">Sonnet · read-only</span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={onClear}
              className="text-[10px] px-2 py-0.5 rounded border border-border-default text-text-secondary hover:bg-bg-secondary/40"
              title="Clear conversation"
            >
              Clear
            </button>
          )}
          <button
            onClick={onToggle}
            className="p-1 rounded hover:bg-bg-secondary/40"
            title="Collapse"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 text-xs">
        {messages.length === 0 && (
          <div className="text-text-secondary">
            <p className="mb-2">Ask in plain English — I'll find the rows for you.</p>
            <div className="space-y-1">
              {[
                "Find matches with Joshua Inglis",
                "How many balls have no video file?",
                "Show me the 5 most recent users",
                "Which model holds player photo URLs?",
              ].map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="block w-full text-left px-2 py-1 rounded border border-border-default/60 hover:bg-bg-secondary/40"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <ChatMessage key={i} msg={m} onOpenPointer={onOpenPointer} />
        ))}

        {busy && (
          <div className="flex items-center gap-2 text-text-secondary">
            <Loader2 className="w-3 h-3 animate-spin" /> thinking…
          </div>
        )}
      </div>

      <div className="p-2 border-t border-border-default">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about the data… (Enter to send, Shift+Enter for newline)"
            rows={2}
            className="flex-1 px-2 py-1.5 text-xs rounded border border-border-default bg-bg-secondary/30 resize-none"
            disabled={busy}
          />
          <button
            onClick={onSend}
            disabled={busy || !input.trim()}
            className="p-2 rounded bg-violet-500/20 hover:bg-violet-500/30 disabled:opacity-40 text-violet-200"
            title="Send"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatMessage({ msg, onOpenPointer }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-lg px-2.5 py-1.5 bg-violet-500/15 text-violet-100 whitespace-pre-wrap break-words">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div
        className={`max-w-[95%] rounded-lg px-2.5 py-1.5 whitespace-pre-wrap break-words ${
          msg.error ? "bg-red-500/15 text-red-200" : "bg-bg-secondary/60"
        }`}
      >
        {msg.content}
      </div>
      {msg.tool_calls?.length > 0 && (
        <div className="space-y-1">
          {msg.tool_calls.map((tc, i) => (
            <ToolCallCard key={i} tc={tc} />
          ))}
        </div>
      )}
      {msg.schema_pointers?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {msg.schema_pointers.map((p, i) => (
            <button
              key={i}
              onClick={() => onOpenPointer(p)}
              className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-violet-400/30 bg-violet-400/10 text-violet-100 hover:bg-violet-400/20"
              title={`Open ${p.app}.${p.model} · ${p.pk}`}
            >
              <ExternalLink className="w-3 h-3" />
              <span className="font-mono">{p.app}.{p.model}</span>
              <span className="text-text-secondary">·</span>
              <span className="truncate max-w-[12rem]">{p.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ToolCallCard({ tc }) {
  const [open, setOpen] = useState(false);
  const r = tc.result_preview || {};
  return (
    <div className="rounded border border-border-default/60 bg-bg-secondary/30 text-[10px]">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 px-2 py-1 hover:bg-bg-secondary/40 text-left"
      >
        {open ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
        <span className="font-mono text-violet-200">{tc.name}</span>
        <span className="flex-1 truncate text-text-secondary">
          {toolCallHeadline(tc.name, r)}
        </span>
      </button>
      {open && (
        <div className="px-2 pb-2 text-text-secondary font-mono whitespace-pre-wrap break-all">
          {JSON.stringify({ args: tc.args, result: r }, null, 2)}
        </div>
      )}
    </div>
  );
}

function toolCallHeadline(name, r) {
  if (r?.error) return `error: ${r.error}`;
  if (name === "count_rows") return `${r.app}.${r.model} → ${r.count}`;
  if (name === "query_rows") return `${r.app}.${r.model} → ${r.returned}/${r.total_matching} rows`;
  if (name === "find_matches") return `${r.returned ?? 0} match(es)`;
  if (name === "get_row") return `${r.app}.${r.model} · ${r.pk}`;
  if (name === "describe_model") return r.summary || "";
  if (name === "list_apps") return r.summary || "";
  return "";
}
